import { Check, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useI18n, type Locale } from "@/i18n/LanguageContext";
import { cn } from "@/lib/utils";

interface LanguageSwitcherProps {
  className?: string;
  /** Renders all languages inline as a segmented control (used in the mobile menu). */
  variant?: "menu" | "segmented";
}

export const LanguageSwitcher = ({ className, variant = "menu" }: LanguageSwitcherProps) => {
  const { locale, setLocale, locales, dictionaries, t } = useI18n();

  if (variant === "segmented") {
    return (
      <div
        role="radiogroup"
        aria-label={t.nav.language}
        className={cn("inline-flex w-full rounded-md border border-border/70 bg-background p-1", className)}
      >
        {locales.map((code) => {
          const active = code === locale;
          return (
            <button
              key={code}
              type="button"
              role="radio"
              aria-checked={active}
              lang={code}
              onClick={() => setLocale(code)}
              className={cn(
                "flex-1 rounded-[calc(var(--radius)-2px)] px-3 py-2 text-sm font-semibold transition-colors",
                active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-highlight",
              )}
            >
              {dictionaries[code].meta.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t.nav.language}
          className={cn("h-10 gap-1.5 px-2.5 font-semibold text-foreground hover:bg-secondary hover:text-highlight", className)}
        >
          <Globe className="h-4 w-4 text-primary" />
          <span className="text-sm uppercase tracking-wide">{locale}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {locales.map((code: Locale) => {
          const active = code === locale;
          return (
            <DropdownMenuItem
              key={code}
              lang={code}
              onSelect={() => setLocale(code)}
              className={cn("flex cursor-pointer items-center justify-between gap-3 py-2.5", active && "text-highlight")}
            >
              <span className="font-medium">{dictionaries[code].meta.name}</span>
              {active ? <Check className="h-4 w-4 text-primary" /> : <span className="text-xs uppercase text-muted-foreground">{code}</span>}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
